import { z } from "zod";
import { fcfa, MOIS, useApiEnveloppe } from "../../api/hooks";
import { EtatChargement, EtatErreur, ListeAvis } from "../../composants/Avis";
import { Tableau, Tuiles } from "../../composants/Tableau";
import type { EcranProps } from "../types";

const SuiviSchema = z.array(
  z.object({
    id: z.string(),
    annee: z.number(),
    mois: z.number(),
    natureCode: z.string(),
    du: z.number().nullable(),
    declare: z.number(),
    paye: z.number(),
    reste: z.number().nullable(),
    refQuittance: z.string().nullable(),
    statutDelai: z.string().nullable(),
    penalite: z.number(),
    observation: z.string().nullable(),
    statutCode: z.string(),
  }),
);

/** Signification des statuts codifiés de la grille déclarative. */
const LEGENDE: Record<string, string> = {
  "D+": "déclaré et payé",
  "D-": "déclaré, non payé",
  "D*": "déclaré, partiellement payé",
  N: "exigible, NON déclaré",
  "TO+": "déclaration à zéro, justifiée",
  "TO-": "déclaration à zéro alors qu'une base existe",
  "-": "non exigible",
  "~": "période non tenue",
};

export function SuiviFiscal({ dossierId, annee, mois }: EcranProps) {
  const q = useApiEnveloppe(
    ["suivi-fiscal", dossierId, annee, mois],
    `/dossiers/${dossierId}/fiscal/suivi?annee=${annee}&mois=${mois}`,
    SuiviSchema,
  );

  if (q.isLoading) return <EtatChargement quoi="du suivi fiscal" />;
  if (q.error) return <EtatErreur erreur={q.error} />;

  const lignes = q.data?.valeur ?? [];

  return (
    <div>
      <p className="resume">
        Une ligne par nature d'impôt, filtrée par régime — et surtout par la{" "}
        <strong>déclaration fiscale d'existence, qui fait foi</strong> : le régime
        donne les natures possibles, la DFE dit lesquelles ont été retenues.
      </p>

      <Tuiles
        tuiles={[
          { titre: "Dû", valeur: fcfa(lignes.reduce((s, l) => s + (l.du ?? 0), 0)) },
          { titre: "Déclaré", valeur: fcfa(lignes.reduce((s, l) => s + l.declare, 0)) },
          { titre: "Payé", valeur: fcfa(lignes.reduce((s, l) => s + l.paye, 0)) },
          {
            titre: "Reste",
            valeur: fcfa(lignes.reduce((s, l) => s + (l.reste ?? 0), 0)),
            alerte: lignes.some((l) => (l.reste ?? 0) > 0),
          },
          {
            titre: "Non déclarées",
            valeur: lignes.filter((l) => l.statutCode === "N").length,
            note: "l'oubli est une minoration déguisée",
            alerte: lignes.some((l) => l.statutCode === "N"),
          },
        ]}
      />

      <section>
        <h2>
          {MOIS[mois - 1]} {annee}
        </h2>
        <Tableau
          colonnes={[
            { cle: "nat", titre: "Nature", rendu: (l) => <code>{l.natureCode}</code> },
            {
              cle: "du",
              titre: "Dû (auto)",
              montant: true,
              // AIRSI est un crédit d'impôt, reversé par le fournisseur :
              // `du = null` signifie « pas à la charge du client ».
              rendu: (l) => (l.du === null ? <em>non exigible</em> : fcfa(l.du)),
            },
            { cle: "dec", titre: "Déclaré", montant: true, rendu: (l) => fcfa(l.declare) },
            { cle: "pay", titre: "Payé", montant: true, rendu: (l) => fcfa(l.paye) },
            {
              cle: "reste",
              titre: "Reste (auto)",
              montant: true,
              rendu: (l) => (l.reste === null ? "—" : fcfa(l.reste)),
            },
            { cle: "qui", titre: "Réf. quittance", rendu: (l) => l.refQuittance ?? "—" },
            { cle: "del", titre: "Statut délai", rendu: (l) => l.statutDelai ?? "—" },
            { cle: "pen", titre: "Pénalité", montant: true, rendu: (l) => fcfa(l.penalite) },
            {
              cle: "st",
              titre: "Statut",
              rendu: (l) => (
                <span
                  className={`statut statut--${l.statutCode === "N" ? "alerte" : "ok"}`}
                  title={LEGENDE[l.statutCode]}
                >
                  {l.statutCode}
                </span>
              ),
            },
            { cle: "obs", titre: "Observation", rendu: (l) => l.observation ?? "—" },
          ]}
          lignes={lignes}
          cle={(l) => l.id}
          vide="Aucune ligne de suivi enregistrée pour ce mois."
        />
      </section>

      <section>
        <h2>Légende des statuts</h2>
        <dl className="legende">
          {Object.entries(LEGENDE).map(([code, sens]) => (
            <div key={code}>
              <dt>{code}</dt>
              <dd>{sens}</dd>
            </div>
          ))}
        </dl>
      </section>

      <ListeAvis avis={q.data?.avis ?? []} />
    </div>
  );
}

// ===========================================================================
// GRILLE DÉCLARATIVE ANNUELLE
// ===========================================================================

const GrilleSchema = z.object({
  annee: z.number(),
  natures: z.array(z.object({ code: z.string(), libelle: z.string(), annuelle: z.boolean() })),
  cases: z.array(z.object({ nature: z.string(), mois: z.number(), statut: z.string() })),
  nbDeclarees: z.number(),
  nbNonDeclarees: z.number(),
  nbNonExigibles: z.number(),
  tauxConformiteDeclarative: z.number(),
});

export function GrilleDeclarative({ dossierId, annee }: EcranProps) {
  const q = useApiEnveloppe(
    ["grille", dossierId, annee],
    `/dossiers/${dossierId}/fiscal/grille?annee=${annee}`,
    GrilleSchema,
  );

  if (q.isLoading) return <EtatChargement quoi="de la grille déclarative" />;
  if (q.error) return <EtatErreur erreur={q.error} />;
  if (!q.data?.valeur) return <p className="a-faire">Aucune donnée déclarative.</p>;

  const g = q.data.valeur;
  const statut = (nature: string, mois: number) =>
    g.cases.find((c) => c.nature === nature && c.mois === mois)?.statut ?? "~";

  return (
    <div>
      <Tuiles
        tuiles={[
          { titre: "Déclarées", valeur: g.nbDeclarees },
          {
            titre: "Non déclarées",
            valeur: g.nbNonDeclarees,
            alerte: g.nbNonDeclarees > 0,
          },
          { titre: "Non exigibles", valeur: g.nbNonExigibles },
          {
            titre: "Taux de conformité déclarative",
            valeur: `${(g.tauxConformiteDeclarative * 100).toFixed(1)} %`,
          },
        ]}
      />

      <section>
        <h2>Grille {g.annee} — nature par mois</h2>
        <div className="tableau-cadre">
          <table className="tableau grille">
            <thead>
              <tr>
                <th>Nature</th>
                {MOIS.map((m) => (
                  <th key={m} className="droite">{m.slice(0, 3)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {g.natures
                .filter((n) => !n.annuelle)
                .map((n) => (
                  <tr key={n.code}>
                    <td title={n.libelle}>
                      <code>{n.code}</code>
                    </td>
                    {MOIS.map((_, i) => {
                      const s = statut(n.code, i + 1);
                      return (
                        <td key={i} className="droite">
                          <span
                            className={`statut statut--${s === "N" || s === "TO-" ? "alerte" : s === "~" ? "neutre" : "ok"}`}
                            title={LEGENDE[s]}
                          >
                            {s}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </section>

      {g.natures.some((n) => n.annuelle) && (
        <section>
          <h2>Natures annuelles</h2>
          <ul>
            {g.natures
              .filter((n) => n.annuelle)
              .map((n) => (
                <li key={n.code}>
                  <code>{n.code}</code> — {n.libelle}
                </li>
              ))}
          </ul>
        </section>
      )}

      <ListeAvis avis={q.data.avis} />
    </div>
  );
}
